import { useState, useEffect } from 'react';
import { supabase } from '@xanectech/supabase-client';
import { toast } from '@xanectech/ui/lib';
import { createLogger, getErrorMessage } from '@xanectech/lib';
import type { Database } from '@xanectech/types/database-types';
const logger = createLogger('useRoadmapItems');

type RoadmapItemRow = Database['public']['Tables']['roadmap_items']['Row'];

const ROADMAP_CATEGORIES: ReadonlyArray<RoadmapItem['category']> = [
  'completed',
  'in_progress',
  'planned',
  'future',
  'vision',
];

const toRoadmapCategory = (value: string | null): RoadmapItem['category'] => {
  if (value && ROADMAP_CATEGORIES.includes(value as RoadmapItem['category'])) {
    return value as RoadmapItem['category'];
  }
  return 'planned';
};

const toRoadmapItem = (row: RoadmapItemRow): RoadmapItem => ({
  id: row.id,
  title: row.title,
  content: row.content,
  category: toRoadmapCategory(row.category),
  expected_date: row.expected_date ?? undefined,
  expected_year: row.expected_year ?? undefined,
  percentage_complete: row.percentage_complete ?? undefined,
  icon: row.icon ?? undefined,
  display_order: row.display_order,
  is_active: row.is_active,
  created_at: row.created_at,
  updated_at: row.updated_at,
  description: row.description ?? undefined,
});

export interface RoadmapItem {
  id: string;
  title: string;
  content: string;
  category: 'completed' | 'in_progress' | 'planned' | 'future' | 'vision';
  expected_date?: string;
  expected_year?: number;
  percentage_complete?: number;
  icon?: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  description?: string;
}

const useRoadmapItems = () => {
  const [items, setItems] = useState<RoadmapItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = async () => {
    try {
      const { data, error } = await supabase
        .from('roadmap_items')
        .select('*')
        .eq('is_active', true)
        .order('category')
        .order('display_order');

      if (error) throw error;
      setItems((data || []).map(toRoadmapItem));
    } catch (error) {
      logger.error('Error fetching roadmap items:', {
        error: getErrorMessage(error),
      });
      toast({
        title: 'Error',
        description: 'Failed to fetch roadmap items',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const createItem = async (
    item: Omit<RoadmapItem, 'id' | 'created_at' | 'updated_at'>,
  ) => {
    try {
      const { data, error } = await supabase
        .from('roadmap_items')
        .insert([item])
        .select()
        .single();

      if (error) throw error;
      if (!data) {
        throw new Error('No roadmap item returned when creating item');
      }

      const mappedItem = toRoadmapItem(data);

      setItems((prev) => [...prev, mappedItem]);
      toast({
        title: 'Success',
        description: 'Roadmap item created successfully',
      });
      return mappedItem;
    } catch (error) {
      logger.error('Error creating roadmap item:', {
        error: getErrorMessage(error),
      });
      toast({
        title: 'Error',
        description: 'Failed to create roadmap item',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const updateItem = async (id: string, updates: Partial<RoadmapItem>) => {
    try {
      const { data, error } = await supabase
        .from('roadmap_items')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      if (!data) {
        throw new Error('No roadmap item returned when updating item');
      }

      const mappedItem = toRoadmapItem(data);

      setItems((prev) =>
        prev.map((item) => (item.id === id ? mappedItem : item)),
      );
      toast({
        title: 'Success',
        description: 'Roadmap item updated successfully',
      });
      return mappedItem;
    } catch (error) {
      logger.error('Error updating roadmap item:', {
        error: getErrorMessage(error),
      });
      toast({
        title: 'Error',
        description: 'Failed to update roadmap item',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const deleteItem = async (id: string) => {
    try {
      const { error } = await supabase
        .from('roadmap_items')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;

      setItems((prev) => prev.filter((item) => item.id !== id));
      toast({
        title: 'Success',
        description: 'Roadmap item deleted successfully',
      });
    } catch (error) {
      logger.error('Error deleting roadmap item:', {
        error: getErrorMessage(error),
      });
      toast({
        title: 'Error',
        description: 'Failed to delete roadmap item',
        variant: 'destructive',
      });
      throw error;
    }
  };

  useEffect(() => {
    void fetchItems();
  }, []);

  return {
    items,
    loading,
    createItem,
    updateItem,
    deleteItem,
    refetch: fetchItems,
  };
};

export { useRoadmapItems };
